import { ReactNode, useEffect, useState } from 'react'
import { Typography } from '@mui/material'
import { NativeAppDetails } from '@playback-rewards/shared-libraries'

//components
import FullPageLoader from 'components/pageLoader'
import MessageLayout from 'components/layout/Message'
//constants
import {
  MINIMUM_SUPPORTED_ANDROID_VERSION_CODE,
  PLAYBACK_ANDROID_APP_ID,
  REACT_NATIVE_STATUS,
  USER_PLATFORM,
} from 'constants/index'
//contexts
import NativeAppContext from './NativeAppContext'
//utils
import { getPlatform, sendRNMessage } from 'utils/utils'

interface NativeAppProviderProps {
  children: ReactNode
}

const NATIVE_DETAILS_TIMEOUT_MS = 5000
const FORCE_UPDATE_EVENT_STORAGE_PREFIX = 'force-update-event'

const sendForceUpdateEventOnce = (eventName: string, versionCode?: number | string) => {
  const currentVersionCode = String(versionCode ?? 'unknown')
  const storageKey = `${FORCE_UPDATE_EVENT_STORAGE_PREFIX}:${MINIMUM_SUPPORTED_ANDROID_VERSION_CODE}:${currentVersionCode}:${eventName}`

  if (localStorage.getItem(storageKey)) return

  localStorage.setItem(storageKey, 'true')
  sendRNMessage({
    status: REACT_NATIVE_STATUS.SEND_EVENT_APPSFLYER,
    eventName,
    eventValue: {
      current_version_code: currentVersionCode,
      minimum_supported_version_code: String(MINIMUM_SUPPORTED_ANDROID_VERSION_CODE),
    },
  })
}

export const NativeAppProvider = ({ children }: NativeAppProviderProps) => {
  const [nativeAppDetails, setNativeAppDetails] = useState<NativeAppDetails | null>(null)
  const isNativeApp = Boolean(window.ReactNativeWebView)
  const shouldCheckVersion =
    isNativeApp &&
    getPlatform() === USER_PLATFORM.ANDROID &&
    MINIMUM_SUPPORTED_ANDROID_VERSION_CODE > 0
  const [isCheckingVersion, setIsCheckingVersion] = useState(shouldCheckVersion)
  const [isUpdateRequired, setIsUpdateRequired] = useState(false)

  useEffect(() => {
    if (!isNativeApp) {
      return
    }

    let timeout: ReturnType<typeof setTimeout> | undefined
    const handleNativeAppDetails = (event: MessageEvent) => {
      if (event.data?.status !== REACT_NATIVE_STATUS.GET_NATIVE_APP_DETAILS) {
        return
      }

      const details = event.data.data as NativeAppDetails
      setNativeAppDetails(details)

      if (shouldCheckVersion) {
        const versionCode = Number(details?.versionCode)
        clearTimeout(timeout)
        setIsUpdateRequired(
          !Number.isFinite(versionCode) || versionCode < MINIMUM_SUPPORTED_ANDROID_VERSION_CODE
        )
        setIsCheckingVersion(false)
      }
    }

    window.addEventListener('message', handleNativeAppDetails)
    sendRNMessage({ status: REACT_NATIVE_STATUS.GET_NATIVE_APP_DETAILS })

    if (shouldCheckVersion) {
      // ponytail: no bridge response means legacy; add an explicit capability flag if false positives appear.
      timeout = setTimeout(() => {
        setIsUpdateRequired(true)
        setIsCheckingVersion(false)
      }, NATIVE_DETAILS_TIMEOUT_MS)
    }

    return () => {
      clearTimeout(timeout)
      window.removeEventListener('message', handleNativeAppDetails)
    }
  }, [isNativeApp, shouldCheckVersion])

  useEffect(() => {
    if (isUpdateRequired) {
      sendForceUpdateEventOnce('force_update_viewed', nativeAppDetails?.versionCode)
    }
  }, [isUpdateRequired, nativeAppDetails?.versionCode])

  if (isCheckingVersion) {
    return <FullPageLoader isVisible />
  }

  if (isUpdateRequired) {
    return (
      <MessageLayout
        className="updateRequiredMessage"
        style={{ backgroundImage: "url('/images/background.png')" }}
        title="Update required"
        imageSrc="/images/update-app.png"
        button={{
          text: 'Update now',
          handleClick: () => {
            sendForceUpdateEventOnce('force_update_clicked', nativeAppDetails?.versionCode)
            window.location.assign(
              `https://play.google.com/store/apps/details?id=${PLAYBACK_ANDROID_APP_ID}`
            )
          },
        }}
      >
        <Typography variant="body1">Please update Playback Rewards to continue.</Typography>
      </MessageLayout>
    )
  }

  return (
    <NativeAppContext.Provider value={{ nativeAppDetails, setNativeAppDetails }}>
      {children}
    </NativeAppContext.Provider>
  )
}
